import { gql } from '@apollo/client';
import type {
  FuelCardAssociationResponse,
  FuelCardAssociationStatusInput,
  FuelCardAssociationStatusResponse,
  ImportBatchResponse,
  ImportFuelTransactionsBatchInput,
  UpdateFuelCardAssociationInput,
  ValidateFuelTransactionsImportInput,
  ValidateFuelTransactionsImportResponse,
} from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';

export class GraphQLFuelCardRequester {
  public validateFuelTransactionsImport = (input: ValidateFuelTransactionsImportInput): Promise<ValidateFuelTransactionsImportResponse> =>
    graphQLRequester
      .query<{
        validateFuelTransactionsImport: ValidateFuelTransactionsImportResponse;
      }>({
        query: gql`
          query ValidateFuelTransactionsImport($input: ValidateFuelTransactionsImportInput!) {
            validateFuelTransactionsImport(input: $input) {
              validRows {
                licensePlate
                transactionTime
                odometerInKm
                fuelType
                quantityInLiters
                totalCost
                merchant {
                  cnpj
                  name
                  city
                  state
                }
              }
              invalidRows {
                data {
                  licensePlate
                  transactionTime
                  odometer
                  fuelType
                  quantityInLiters
                  totalCost
                  merchantName
                  merchantCnpj
                  merchantCity
                  merchantState
                  rowIndex
                }
                errors {
                  error
                  column {
                    name
                    position
                  }
                }
              }
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then((response) => response.validateFuelTransactionsImport);

  public importFuelTransactions = (input: ImportFuelTransactionsBatchInput[]): Promise<ImportBatchResponse> =>
    graphQLRequester
      .mutate<{ createdTransactions: ImportBatchResponse }, { input: ImportFuelTransactionsBatchInput[] }>({
        mutation: gql`
          mutation ImportFuelTransactions($input: [ImportFuelTransactionsBatchInput!]!) {
            importFuelTransactions(input: $input) {
              count
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then((response) => response.createdTransactions);

  public getFuelCardAssociations = (status?: string | null): Promise<FuelCardAssociationResponse[]> =>
    graphQLRequester
      .query<{
        fuelCardAssociations: FuelCardAssociationResponse[];
      }>({
        query: gql`
          query FuelCardAssociations($input: String) {
            fuelCardAssociations(input: $input) {
              id
              fleetId
              fuelCardId
              updatedAt
              createdAt
              deletedAt
              providerLicensePlate
              providerName
              vehicle {
                id
                licensePlate
              }
            }
          }
        `,
        variables: {
          input: status,
        },
      })
      .then((response) => response.fuelCardAssociations);

  public updateFuelCardAssociation = (input: UpdateFuelCardAssociationInput): Promise<FuelCardAssociationStatusResponse> =>
    graphQLRequester
      .mutate<{ updateFuelCardAssociation: FuelCardAssociationStatusResponse }, { input: UpdateFuelCardAssociationInput }>({
        mutation: gql`
          mutation UpdateFuelCardAssociation($input: UpdateFuelCardAssociationInput!) {
            updateFuelCardAssociation(input: $input) {
              message
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then((response) => response.updateFuelCardAssociation);

  public deleteFuelCardAssociation = (input: FuelCardAssociationStatusInput): Promise<FuelCardAssociationStatusResponse> =>
    graphQLRequester
      .mutate<{ deleteFuelCardAssociation: FuelCardAssociationStatusResponse }, { input: FuelCardAssociationStatusInput }>({
        mutation: gql`
          mutation DeleteFuelCardAssociation($input: FuelCardAssociationStatusInput!) {
            deleteFuelCardAssociation(input: $input) {
              message
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then((response) => response.deleteFuelCardAssociation);

  public restoreFuelCardAssociation = (input: FuelCardAssociationStatusInput): Promise<FuelCardAssociationStatusResponse> =>
    graphQLRequester
      .mutate<{ restoreFuelCardAssociation: FuelCardAssociationStatusResponse }, { input: FuelCardAssociationStatusInput }>({
        mutation: gql`
          mutation RestoreFuelCardAssociation($input: FuelCardAssociationStatusInput!) {
            restoreFuelCardAssociation(input: $input) {
              message
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then((response) => response.restoreFuelCardAssociation);
}

export default GraphQLFuelCardRequester;
